import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
    Shield, CheckCircle, AlertCircle, Loader, 
    ExternalLink, Users, Settings, Zap,
    Database, Link
} from 'lucide-react';
import { useToastContext } from '../../contexts/ToastContext';
import { useAuth } from '../../context/UnifiedAuthContext.jsx';
import apiClient from '../../utils/api.js';
import './GHLConfig.scss';

const GHLConfig = () => {
    const [searchParams] = useSearchParams();
    const { showSuccess, showError } = useToastContext();
    const { urlParams } = useAuth();
    
    const locationId = urlParams.location || searchParams.get('location');
    const integrationId = searchParams.get('integration');
    
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [integration, setIntegration] = useState(null);
    const [pestpacCredentials, setPestpacCredentials] = useState({
        clientId: '',
        clientSecret: '',
        tenantId: '',
        apiKey: '',
        username: '',
        password: ''
    });
    
    useEffect(() => {
        if (locationId) {
            loadIntegration();
        }
    }, [locationId]);

    const loadIntegration = async () => {
        try {
            setLoading(true);
            const data = await apiClient.get(
                apiClient.addUrlParams(`/api/integration/${encodeURIComponent(locationId)}`)
            );
            
            if (data.success) {
                setIntegration(data.data);
                
                // If PestPac is already configured, load those credentials
                if (data.data.has_pestpac_config) {
                    // Note: In production, you might want to only load non-sensitive data
                    // or show that credentials are configured without showing the actual values
                }
            } else {
                showError('Failed to load integration details');
            }
        } catch (error) {
            console.error('Error loading integration:', error);
            showError('Failed to load integration details');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (field, value) => {
        setPestpacCredentials(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const testPestPacConnection = async () => {
        try {
            setTesting(true);
            
            const result = await apiClient.post(
                apiClient.addUrlParams('/api/pestpac/test'),
                pestpacCredentials
            );
            
            if (result.success) {
                showSuccess('PestPac connection successful!');
                return true;
            } else {
                showError(`Connection failed: ${result.error}`);
                return false;
            }
        } catch (error) {
            console.error('Error testing connection:', error);
            showError('Failed to test connection');
            return false;
        } finally {
            setTesting(false);
        }
    };

    const savePestPacConfig = async () => {
        try {
            setSaving(true);
            
            // Validate required fields
            const requiredFields = ['clientId', 'clientSecret', 'tenantId', 'apiKey', 'username', 'password'];
            const missingFields = requiredFields.filter(field => !pestpacCredentials[field].trim());
            
            if (missingFields.length > 0) {
                showError(`Please fill in all required fields: ${missingFields.join(', ')}`);
                return;
            }

            const result = await apiClient.post(
                apiClient.addUrlParams(`/api/integration/${encodeURIComponent(locationId)}/pestpac`),
                pestpacCredentials
            );
            
            if (result.success) {
                showSuccess('PestPac integration configured successfully!');
                await loadIntegration(); // Reload to show updated status
            } else {
                showError(`Configuration failed: ${result.error}`);
            }
        } catch (error) {
            console.error('Error saving configuration:', error);
            showError('Failed to save configuration');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="ghl-config loading">
                <div className="loading-container">
                    <Loader className="spin" size={48} />
                    <h2>Loading Configuration...</h2>
                    <p>Setting up your Ejento Elevate integration</p>
                </div>
            </div>
        );
    }

    if (!integration) {
        return (
            <div className="ghl-config error">
                <div className="error-container">
                    <AlertCircle size={48} />
                    <h2>Integration Not Found</h2>
                    <p>Unable to find the integration for location {locationId}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="ghl-config">
            <div className="ghl-config-container">
                <motion.div 
                    className="config-header"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                >
                    <div className="header-content">
                        <div className="logo-section">
                            <Zap className="logo" />
                            <div>
                                <h1>Ejento Elevate</h1>
                                <p>AI-Powered Review Management System</p>
                            </div>
                        </div>
                        
                        <div className="status-badge">
                            <CheckCircle className="success-icon" />
                            <span>GoHighLevel Connected</span>
                        </div>
                    </div>
                </motion.div>

                <motion.div 
                    className="integration-status"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                >
                    <h3><Shield /> Integration Status</h3>
                    <div className="status-grid">
                        <div className="status-item">
                            <Link className="status-icon success" />
                            <div>
                                <strong>GoHighLevel</strong>
                                <span>Connected successfully</span>
                            </div>
                        </div>
                        <div className="status-item">
                            <Users className="status-icon" />
                            <div>
                                <strong>{integration.users_count} Users</strong>
                                <span>Synced from your location</span>
                            </div>
                        </div>
                        <div className="status-item">
                            <Database className={`status-icon ${integration.has_pestpac_config ? 'success' : 'warning'}`} />
                            <div>
                                <strong>PestPac</strong>
                                <span>{integration.has_pestpac_config ? 'Configured' : 'Needs Configuration'}</span>
                            </div>
                        </div>
                    </div>
                </motion.div>

                <motion.div 
                    className="pestpac-config"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                >
                    <div className="config-header">
                        <h3><Settings /> PestPac Configuration</h3>
                        <p>Connect your PestPac account to enable automatic review solicitation</p>
                    </div>

                    <div className="credentials-form">
                        <div className="form-grid">
                            <div className="form-group">
                                <label>Client ID*</label>
                                <input
                                    type="text"
                                    value={pestpacCredentials.clientId}
                                    onChange={(e) => handleInputChange('clientId', e.target.value)}
                                    placeholder="Your PestPac OAuth Client ID"
                                />
                            </div>

                            <div className="form-group">
                                <label>Client Secret*</label>
                                <input
                                    type="password"
                                    value={pestpacCredentials.clientSecret}
                                    onChange={(e) => handleInputChange('clientSecret', e.target.value)}
                                    placeholder="Your PestPac OAuth Client Secret"
                                />
                            </div>

                            <div className="form-group">
                                <label>Tenant ID*</label>
                                <input
                                    type="text"
                                    value={pestpacCredentials.tenantId}
                                    onChange={(e) => handleInputChange('tenantId', e.target.value)}
                                    placeholder="Your PestPac Tenant ID"
                                />
                            </div>

                            <div className="form-group">
                                <label>API Key*</label>
                                <input
                                    type="text"
                                    value={pestpacCredentials.apiKey}
                                    onChange={(e) => handleInputChange('apiKey', e.target.value)}
                                    placeholder="Your PestPac API Key"
                                />
                            </div>

                            <div className="form-group">
                                <label>Username*</label>
                                <input
                                    type="email"
                                    value={pestpacCredentials.username}
                                    onChange={(e) => handleInputChange('username', e.target.value)}
                                    placeholder="Your PestPac username/email"
                                />
                            </div>

                            <div className="form-group">
                                <label>Password*</label>
                                <input
                                    type="password"
                                    value={pestpacCredentials.password}
                                    onChange={(e) => handleInputChange('password', e.target.value)}
                                    placeholder="Your PestPac password"
                                />
                            </div>
                        </div>

                        <div className="form-actions">
                            <button
                                type="button"
                                className="btn btn-outline"
                                onClick={testPestPacConnection}
                                disabled={testing}
                            >
                                {testing ? <Loader className="spin" size={16} /> : <ExternalLink size={16} />}
                                Test Connection
                            </button>
                            
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={savePestPacConfig}
                                disabled={saving}
                            >
                                {saving ? <Loader className="spin" size={16} /> : <CheckCircle size={16} />}
                                Save Configuration
                            </button>
                        </div>
                    </div>
                </motion.div>

                <motion.div 
                    className="help-section"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                >
                    <h4>Need Help?</h4>
                    <div className="help-content">
                        <p>
                            To get your PestPac API credentials, contact WorkWave support or check your 
                            PestPac admin panel. You'll need to have API access enabled for your account.
                        </p>
                        <p>
                            Once configured, Ejento Elevate will automatically create review campaigns 
                            when appointments are completed in your PestPac system.
                        </p>
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default GHLConfig;
